import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ErrorPageProps {
  /** "Page not found", "Couldn't load this member", "Something went wrong". */
  title: string;
  /** One or two sentences: what happened and what the person can do. */
  message?: React.ReactNode;
  /** Shows a "Try again" button. */
  onRetry?: () => void;
  /** Where "Back to home" goes. `null` hides it. */
  homeHref?: string | null;
}

/**
 * The one way a page that cannot render says so (#652): an unknown URL, a
 * record that is not there, a load that failed with nothing else to show,
 * or a crash caught by `ErrorBoundary`. Kept identical in front-porch and
 * member-portal — edit both, or neither.
 *
 * Content only, no shell — a page puts it inside its own `SidebarLayout`,
 * and the error boundary shows it bare because the shell may be what
 * broke. For an error beside content that did render, use `ErrorAlert`.
 *
 * In the Linear idiom of the list screens' empty state (#603): quiet,
 * centred, 13px muted text and the small accent / hairline buttons, so a
 * missing page reads as a state of the app rather than an alarm.
 *
 * "Back to home" is a plain link, so following it reloads the app; after
 * a crash that is the point.
 */
export const ErrorPage: React.FC<ErrorPageProps> = ({
  title,
  message,
  onRetry,
  homeHref = '/home',
}) => (
  <div
    role="alert"
    className="mx-auto flex max-w-sm flex-col items-center px-4 py-24 text-center"
  >
    <ExclamationTriangleIcon aria-hidden="true" className="size-8 text-fg-faint" />
    <h1 className="mt-4 text-[14px] font-medium text-fg">{title}</h1>
    {message && (
      <div className="mt-1 text-[13px]/5 text-fg-muted">{message}</div>
    )}
    {(onRetry || homeHref) && (
      <div className="mt-5 flex items-center gap-2">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center rounded-md bg-accent px-2.5 py-1 text-[12px] font-medium text-white hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1"
          >
            Try again
          </button>
        )}
        {homeHref && (
          <a
            href={homeHref}
            className="inline-flex items-center rounded-md border border-line px-2.5 py-1 text-[12px] font-medium text-fg-secondary hover:border-line-strong hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Back to home
          </a>
        )}
      </div>
    )}
  </div>
);

export default ErrorPage;
